import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Loader2, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import SectionLabel from "../components/SectionLabel";
import { submitContact } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent — we'll reply within 2 working days.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-16 lg:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6 fade-up">
            <SectionLabel>Get in touch</SectionLabel>
            <h1 className="font-serif text-5xl lg:text-7xl font-light text-forest leading-[0.95]">
              Say hello, send a <em className="italic text-terracotta">tip</em>, report a rescue.
            </h1>
            <p className="text-lg text-ink/80 leading-relaxed">
              We respond to every email — usually within 24 hours, sometimes from the back of a rescue van. For active emergencies, please call our 24×7 helpline.
            </p>

            <div className="space-y-5 pt-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 shrink-0 rounded-full bg-terracotta/10 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-terracotta" strokeWidth={1.5}/>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-1">24×7 emergency helpline</div>
                  <a href="tel:+917477216753" className="font-serif text-2xl text-forest link-underline" data-testid="contact-phone-link">+91 74772 16753</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 shrink-0 rounded-full bg-terracotta/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-terracotta" strokeWidth={1.5}/>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-1">Email</div>
                  <a href="mailto:pawsomelandfoundation@gmail.com" className="font-serif text-2xl text-forest link-underline break-all" data-testid="contact-email-link">pawsomelandfoundation@gmail.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 shrink-0 rounded-full bg-terracotta/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-terracotta" strokeWidth={1.5}/>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-1">Sanctuary visits</div>
                  <p className="font-serif text-xl text-forest leading-snug">Waterfall Road<br/>Near Gaushala Barkhedi Obdullah<br/>Bhadbhada Ghat, Bhopal</p>
                  <p className="text-sm text-ink/60 mt-1">Open Sat & Sun, 10am – 4pm (by appointment)</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-office-address">
                <div className="w-11 h-11 shrink-0 rounded-full bg-terracotta/10 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-terracotta" strokeWidth={1.5}/>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-1">Foundation office</div>
                  <p className="font-serif text-xl text-forest leading-snug">House no. 20, Kaveri Tailors<br/>Ganesh Mandir, Chhola Road<br/>Bhopal</p>
                  <p className="text-sm text-ink/60 mt-1">For documents, paperwork & in-person meetings</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="lg:col-span-7 lg:self-start bg-white rounded-[2rem] border border-bone-soft p-8 lg:p-12 space-y-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="contact-form">
            <h2 className="font-serif text-3xl text-forest mb-6">Send us a message</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name" className="text-sm text-ink/70 mb-2 block">Name *</Label>
                <Input id="c-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 rounded-xl" data-testid="contact-name-input"/>
              </div>
              <div>
                <Label htmlFor="c-email" className="text-sm text-ink/70 mb-2 block">Email *</Label>
                <Input id="c-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12 rounded-xl" data-testid="contact-email-input"/>
              </div>
            </div>
            <div>
              <Label htmlFor="c-subj" className="text-sm text-ink/70 mb-2 block">Subject *</Label>
              <Input id="c-subj" required value={form.subject} onChange={(e) => update("subject", e.target.value)} className="h-12 rounded-xl" data-testid="contact-subject-input"/>
            </div>
            <div>
              <Label htmlFor="c-msg" className="text-sm text-ink/70 mb-2 block">Message *</Label>
              <Textarea id="c-msg" rows={6} required value={form.message} onChange={(e) => update("message", e.target.value)} className="rounded-xl" data-testid="contact-message-input"/>
            </div>
            <Button type="submit" disabled={loading} className="bg-terracotta hover:bg-terracotta-deep text-bone rounded-full h-13 px-8 py-3.5 font-semibold" data-testid="contact-submit-button">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Sending…</>) : "Send message"}
            </Button>
          </form>
        </div>
      </section>

      {/* MAP PLACEHOLDER */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="rounded-[2rem] overflow-hidden border border-bone-soft aspect-[16/8]">
            <iframe
              title="Pawsomeland Sanctuary Location"
              src="https://maps.google.com/maps?q=Pawsomeland+Foundation+Shelter%4023.3954714,77.5037843&z=17&t=m&output=embed&iwloc=near"
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
